/**
 * useViewTracking Hook
 * 
 * Provides IntersectionObserver-based view tracking for item cards.
 * Tracks when items are 60% visible for 800ms.
 */

import { useEffect, useRef, useCallback } from 'react';
import { createViewObserver, resetViewedItems, flushTracking } from '../utils/tracker';

/**
 * Hook for tracking item views using IntersectionObserver
 * @param {string} category - The category of items
 * @param {string[]} itemIds - Array of item IDs being displayed
 * @returns {Object} - { observerRef, observe, unobserve }
 */
export default function useViewTracking(category, itemIds = []) {
  const observerRef = useRef(null);
  const observedRef = useRef(new Set());
  
  // Create observer on mount or category change
  useEffect(() => {
    // Reset viewed items when category changes
    resetViewedItems();
    
    // Create new observer
    observerRef.current = createViewObserver(category);
    
    return () => {
      // Cleanup observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      // Flush pending events
      flushTracking();
    };
  }, [category]);
  
  // Observe an element
  const observe = useCallback((element) => {
    if (element && observerRef.current) {
      const itemId = element.dataset?.itemId;
      if (itemId && !observedRef.current.has(itemId)) {
        observerRef.current.observe(element);
        observedRef.current.add(itemId);
      }
    }
  }, []);
  
  // Unobserve an element
  const unobserve = useCallback((element) => {
    if (element && observerRef.current) {
      observerRef.current.unobserve(element);
      const itemId = element.dataset?.itemId;
      if (itemId) {
        observedRef.current.delete(itemId);
      }
    }
  }, []);
  
  // Callback ref for easy use on cards
  const cardRef = useCallback((element) => {
    if (element) {
      observe(element);
    }
  }, [observe]);
  
  return {
    observerRef,
    observe,
    unobserve,
    cardRef,
  };
}
